import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import PlayerAvatar from "@/components/app/PlayerAvatar";
import RecordShareCard from "@/components/app/RecordShareCard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Crown, Sparkles, Star, Calendar, Target } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1546519638-68e109498ffc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxiYXNrZXRiYWxsJTIwYXJlbmF8ZW58MHx8fHwxNzgyNjA3MjkzfDA&ixlib=rb-4.1.0&q=85&w=1600";
const ALL_TIME = "__all__";

const SINGLE_GAME = [
  { key: "points", label: "Points in a Game", suffix: " PTS" },
  { key: "rebounds", label: "Rebounds in a Game", suffix: " REB" },
  { key: "assists", label: "Assists in a Game", suffix: " AST" },
  { key: "steals", label: "Steals in a Game", suffix: " STL" },
  { key: "blocks", label: "Blocks in a Game", suffix: " BLK" },
  { key: "three_pointers", label: "3-Pointers in a Game", suffix: " 3PM" },
];

const SEASON_AVGS = [
  { key: "ppg", label: "Points Per Game (Season)", suffix: " PPG" },
  { key: "reb_avg", label: "Rebounds Per Game (Season)", suffix: " RPG" },
  { key: "ast_avg", label: "Assists Per Game (Season)", suffix: " APG" },
  { key: "stl_avg", label: "Steals Per Game (Season)", suffix: " SPG" },
  { key: "blk_avg", label: "Blocks Per Game (Season)", suffix: " BPG" },
];

const CAREER_TOTALS = [
  { key: "total_points", label: "Career Points", suffix: " PTS" },
  { key: "rebounds", label: "Career Rebounds", suffix: " REB" },
  { key: "assists", label: "Career Assists", suffix: " AST" },
  { key: "steals", label: "Career Steals", suffix: " STL" },
  { key: "blocks", label: "Career Blocks", suffix: " BLK" },
  { key: "gp", label: "Career Games", suffix: " GP" },
];

export default function Records() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [seasonFilter, setSeasonFilter] = useState(ALL_TIME);

  useEffect(() => {
    const params = seasonFilter !== ALL_TIME ? { season: seasonFilter } : {};
    api.get("/records", { params }).then((r) => setData(r.data)).catch(() => {});
  }, [seasonFilter]);

  if (!data) return <div className="p-10 text-ink-300">Loading the record book…</div>;

  return (
    <div data-testid="hall-of-records">
      <div className="relative h-56 md:h-72 overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-950/40 via-ink-950/80 to-ink-950" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_right,rgba(107,15,26,0.4),transparent_55%)]" />
        <div className="relative h-full px-6 md:px-12 flex flex-col justify-end pb-8">
          <p className="eyebrow flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> The Canucks Legacy
          </p>
          <h1 className="heading-1 flex items-center gap-3 mt-1">
            <Crown className="w-8 h-8 text-burgundy" />
            Hall of <span className="text-burgundy">Records</span>
          </h1>
          <p className="text-ink-300 text-sm mt-2 max-w-lg">
            Every record that&apos;s been broken under the burgundy and black. Updated live as new stats are posted.
          </p>
        </div>
      </div>

      <div className="px-6 md:px-12 py-8">
        {/* Season filter */}
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-ink-400" />
            <span className="text-[10px] uppercase tracking-wider text-ink-400 font-bold">View</span>
          </div>
          <Select value={seasonFilter} onValueChange={setSeasonFilter}>
            <SelectTrigger className="w-[180px] bg-ink-900 border-ink-700 text-white font-display font-bold uppercase tracking-wider h-9" data-testid="records-season-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-ink-900 border-ink-700">
              <SelectItem value={ALL_TIME} className="text-white">All-Time</SelectItem>
              {(data.available_seasons || []).map((s) => (
                <SelectItem key={s} value={s} className="text-white">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {seasonFilter !== ALL_TIME && (
            <span className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 px-2 py-1 rounded uppercase tracking-wider">
              Filtered to {seasonFilter}
            </span>
          )}
        </div>

        <Tabs defaultValue="single_game">
          <TabsList className="bg-ink-800 border border-ink-700 flex-wrap h-auto">
            <TabsTrigger value="single_game" className="data-[state=active]:bg-burgundy data-[state=active]:text-white" data-testid="tab-single-game">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Single-Game
            </TabsTrigger>
            <TabsTrigger value="season" className="data-[state=active]:bg-burgundy data-[state=active]:text-white" data-testid="tab-season">
              <Calendar className="w-3.5 h-3.5 mr-1.5" /> Season Bests
            </TabsTrigger>
            <TabsTrigger value="career" className="data-[state=active]:bg-burgundy data-[state=active]:text-white" data-testid="tab-career">
              <Star className="w-3.5 h-3.5 mr-1.5" /> Career
            </TabsTrigger>
          </TabsList>

          <TabsContent value="single_game" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {SINGLE_GAME.map((cat) => (
                <RecordCard
                  key={cat.key}
                  category="single"
                  cat={cat}
                  rows={data.single_game[cat.key] || []}
                  activeTarget={(data.active_targets || {})[cat.key]}
                  canShare={!!user}
                />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="season" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {SEASON_AVGS.map((cat) => (
                <RecordCard
                  key={cat.key}
                  category="season"
                  cat={cat}
                  rows={data.season_avgs[cat.key] || []}
                />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="career" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {CAREER_TOTALS.map((cat) => (
                <RecordCard
                  key={cat.key}
                  category="career"
                  cat={cat}
                  rows={data.career_totals[cat.key] || []}
                />
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatusPill({ player }) {
  if (!player) return null;
  if (player.status === "active") {
    return (
      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 rounded px-1.5 py-0.5">
        Active
      </span>
    );
  }
  if (player.graduation_year) {
    return (
      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold bg-amber-500/10 text-amber-300 border border-amber-500/30 rounded px-1.5 py-0.5">
        &apos;{String(player.graduation_year).slice(-2)}
      </span>
    );
  }
  return null;
}

function RecordCard({ cat, rows, category, activeTarget, canShare = false }) {
  // If the #1 record isn't held by an active player AND we have an active mark to chase, show it as a target banner
  const leaderIsActive = rows[0]?.player?.status === "active";
  const showTarget = category === "single" && !leaderIsActive && activeTarget && activeTarget.player;
  const top = rows[0];
  const showShare = canShare && category === "single" && top;

  // Build a record object matching the RecordShareCard shape from the #1 row
  const topRecord = showShare ? {
    category: cat.key,
    label: cat.label,
    suffix: cat.suffix.trim(),
    value: top.value,
    rank: 1,
    player_name: top.player?.player_name,
    slug: top.player?.slug,
    jersey_number: top.player?.jersey_number,
    opponent: top.game?.opponent,
    date: top.game?.date,
    home_away: top.game?.home_away,
    season: top.game?.season,
  } : null;

  return (
    <div className="bg-ink-800 rounded-lg border border-white/5 overflow-hidden" data-testid={`record-card-${cat.key}`}>
      <div className="px-5 py-3 bg-ink-900 border-b border-ink-700/60 flex items-center justify-between gap-2 flex-wrap">
        <p className="eyebrow text-[10px]">{cat.label}</p>
        <div className="flex items-center gap-3 flex-wrap">
          {showTarget && (
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider">
              <Target className="w-3 h-3 text-emerald-400" />
              <span className="text-ink-400">Active target:</span>
              <Link to={`/player/${activeTarget.player.slug}`} className="text-emerald-300 font-bold hover:text-emerald-200 transition" data-testid={`active-target-${cat.key}`}>
                {activeTarget.player.player_name?.split(" ").slice(-1)[0]} · {activeTarget.value}{cat.suffix}
              </Link>
            </div>
          )}
          {topRecord && <RecordShareCard record={topRecord} compact />}
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="text-ink-400 text-sm py-8 text-center">No record on the books yet — be the first.</p>
      ) : (
        <div>
          {rows.map((r, i) => {
            const top = i === 0;
            const value = category === "single" ? r.value : r[cat.key];
            return (
              <Link
                to={r.player?.slug ? `/player/${r.player.slug}` : "#"}
                key={i}
                className={`flex items-center gap-4 px-5 py-3 border-b border-ink-700/30 last:border-0 hover:bg-ink-900/50 transition ${top ? "bg-burgundy/15" : ""}`}
                data-testid={`record-${cat.key}-${i}`}
              >
                <div className={`w-7 text-center font-display text-xl font-black ${top ? "text-burgundy" : "text-ink-400"}`}>
                  {top ? <Crown className="w-5 h-5 inline" /> : `#${i + 1}`}
                </div>
                <PlayerAvatar player={r.player} size="sm" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-display font-bold uppercase leading-tight truncate">{r.player?.player_name || "Unknown"}</p>
                    <StatusPill player={r.player} />
                  </div>
                  <p className="text-[10px] text-ink-400 truncate">
                    {category === "single" && r.game && (
                      <>{r.game.home_away === "home" ? "vs" : "@"} {r.game.opponent} · {r.game.date}{r.game.season ? ` · ${r.game.season}` : ""}</>
                    )}
                    {category === "season" && r.season && <>{r.season} · {r.gp} GP</>}
                    {category === "career" && <>{r.gp} GP{r.player?.graduation_year ? ` · Class of ${r.player.graduation_year}` : r.player?.status === "active" ? " · Active" : ""}</>}
                  </p>
                </div>
                <p className={`font-display text-2xl md:text-3xl font-black shrink-0 ${top ? "text-burgundy" : "text-white"}`}>
                  {value}<span className="text-[10px] text-ink-400 font-bold">{cat.suffix}</span>
                </p>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
